import React, { useState, useEffect } from 'react';
import { Heart, AlertCircle, Loader, CheckCircle } from 'lucide-react';
import { getPlayerProfile, getUsernameFromProfileId } from '../../../../services/firestoreService';
import { createDonationCheckoutSession } from '../../../../services/stripeService';
import useBlockStyles from '../common/useBlockStyles';

/**
 * DonationsRenderer - Renders the donations block
 * Themed donation section with Stripe checkout integration
 */
const STYLE_OPTIONS = {
  defaultBackground: 'section',
  defaultHeadingSize: 'medium',
  defaultAlignment: 'center',
  defaultButtonStyle: 'primary',
  defaultPadding: 'normal',
};

const DonationsRenderer = ({ block, profileId, theme = {} }) => {
  const { content } = block;
  const {
    heading = 'Support My Work',
    description = '',
    buttonText = 'Donate',
  } = content;

  const [profile, setProfile] = useState(null);
  const [username, setUsername] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [donationAmount, setDonationAmount] = useState(5);
  const [customAmount, setCustomAmount] = useState('');
  const [donorEmail, setDonorEmail] = useState('');
  const [donorMessage, setDonorMessage] = useState('');
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [processing, setProcessing] = useState(false);

  const styles = useBlockStyles(content, theme, STYLE_OPTIONS);

  const presetAmounts = [2, 5, 20, 100];

  useEffect(() => {
    const loadProfile = async () => {
      if (!profileId) {
        setLoading(false);
        return;
      }

      try {
        const [fetchedProfile, fetchedUsername] = await Promise.all([
          getPlayerProfile(profileId),
          getUsernameFromProfileId(profileId),
        ]);
        setProfile(fetchedProfile);
        setUsername(fetchedUsername);
      } catch (error) {
        console.error('Error loading profile:', error);
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, [profileId]);

  const handleDonate = async () => {
    const amount = customAmount ? parseFloat(customAmount) : donationAmount;

    if (!amount || amount < 2) {
      alert('Please enter a valid amount ($2 minimum)');
      return;
    }

    if (!donorEmail && !isAnonymous) {
      alert('Please enter your email or donate anonymously');
      return;
    }

    setProcessing(true);

    try {
      const { sessionUrl } = await createDonationCheckoutSession(
        profileId,
        Math.round(amount * 100), // Convert to cents
        isAnonymous ? '' : donorEmail,
        donorMessage
      );

      // Redirect to Stripe Checkout
      window.location.href = sessionUrl;
    } catch (error) {
      console.error('Donation error:', error);
      alert(error.message || 'Unable to process donation. Please try again.');
      setProcessing(false);
    }
  };

  // Placeholder when donations not configured
  if (!loading && (!profile?.stripeAccountId || !profile?.donationsEnabled)) {
    return (
      <div
        className={`w-full ${styles.sectionPaddingClass}`}
        style={{ backgroundColor: styles.sectionBackground }}
      >
        <div className="max-w-lg mx-auto text-center">

          <h3
            className="text-xl font-semibold mb-2"
            style={{ color: styles.headingColor, fontFamily: styles.headingFontStyle }}
          >
            Donations Coming Soon
          </h3>
          <p style={{ color: styles.mutedColor }}>
            Donations are not yet configured for this profile.
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div
        className={`w-full ${styles.sectionPaddingClass}`}
        style={{ backgroundColor: styles.sectionBackground }}
      >
        <div className="max-w-4xl mx-auto flex items-center justify-center">
          <div className="animate-spin w-8 h-8 border-3 border-gray-600 rounded-full" style={{ borderTopColor: styles.accentColor }}></div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div
        className={`w-full ${styles.sectionPaddingClass}`}
        style={{ backgroundColor: styles.sectionBackground }}
      >
        <div className="max-w-xl mx-auto text-center">


          {/* Heading */}
          {heading && (
            <h2
              className="text-2xl sm:text-3xl font-bold mb-3"
              style={{ color: styles.headingColor, fontFamily: styles.headingFontStyle }}
            >
              {heading}
            </h2>
          )}

          {/* Description */}
          {(description || profile?.donationMessage) && (
            <p className="whitespace-pre-wrap mb-8 max-w-md mx-auto" style={{ color: styles.mutedColor }}>
              {description || profile?.donationMessage}
            </p>
          )}

          {/* Donate Button */}
          <button
            onClick={() => setShowModal(true)}
            className="px-8 py-3 rounded-lg font-semibold text-lg inline-flex items-center gap-2 transition-all hover:opacity-90 hover:translate-y-[-2px]"
            style={styles.getButtonStyles()}
          >
            <Heart size={20} />
            {buttonText}
          </button>
        </div>
      </div>

      {/* Donation Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/70"
            onClick={() => !processing && setShowModal(false)}
          ></div>
          <div
            className="relative w-full max-w-md rounded-xl p-6 max-h-[90vh] overflow-y-auto"
            style={{ backgroundColor: styles.cardBackground }}
          >
            {/* Modal Header */}
            <div className="text-center mb-6">
              <h3
                className="text-xl font-bold mb-1"
                style={{ color: styles.headingColor }}
              >
                Support {profile?.displayName || 'this creator'}
              </h3>
              <p className="text-sm" style={{ color: styles.mutedColor }}>
                Choose an amount to donate
              </p>
            </div>

            {/* Preset Amounts */}
            <div className="grid grid-cols-4 gap-2 mb-4">
              {presetAmounts.map((amount) => (
                <button
                  key={amount}
                  onClick={() => {
                    setDonationAmount(amount);
                    setCustomAmount('');
                  }}
                  className="py-3 rounded-lg font-semibold transition-all"
                  style={{
                    backgroundColor: donationAmount === amount && !customAmount ? styles.accentColor : styles.inputBackground,
                    color: donationAmount === amount && !customAmount ? '#ffffff' : styles.headingColor,
                    border: `1px solid ${donationAmount === amount && !customAmount ? styles.accentColor : 'rgba(255,255,255,0.1)'}`,
                  }}
                >
                  ${amount}
                </button>
              ))}
            </div>

            {/* Custom Amount */}
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2" style={{ color: styles.bodyColor }}>
                Custom Amount
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2" style={{ color: styles.mutedColor }}>$</span>
                <input
                  type="number"
                  min="2"
                  max="500"
                  step="0.01"
                  value={customAmount}
                  onChange={(e) => setCustomAmount(e.target.value)}
                  placeholder="Enter amount"
                  className="w-full pl-8 pr-4 py-3 rounded-lg border focus:outline-none"
                  style={{
                    backgroundColor: styles.inputBackground,
                    borderColor: customAmount ? styles.accentColor : 'rgba(255,255,255,0.1)',
                    color: styles.headingColor,
                  }}
                />
              </div>
            </div>

            {/* Email */}
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2" style={{ color: styles.bodyColor }}>
                Email {!isAnonymous && <span className="text-red-400">*</span>}
              </label>
              <input
                type="email"
                value={donorEmail}
                onChange={(e) => setDonorEmail(e.target.value)}
                disabled={isAnonymous}
                placeholder={isAnonymous ? 'Donating anonymously' : 'your@email.com'}
                className="w-full px-4 py-3 rounded-lg border focus:outline-none disabled:opacity-50"
                style={{
                  backgroundColor: styles.inputBackground,
                  borderColor: 'rgba(255,255,255,0.1)',
                  color: styles.headingColor,
                }}
              />
            </div>

            {/* Anonymous Toggle */}
            <label className="flex items-center gap-3 mb-4 cursor-pointer">
              <input
                type="checkbox"
                checked={isAnonymous}
                onChange={(e) => setIsAnonymous(e.target.checked)}
                style={{ accentColor: styles.accentColor }}
              />
              <span className="text-sm" style={{ color: styles.mutedColor }}>
                Donate anonymously
              </span>
            </label>

            {/* Message */}
            <div className="mb-6">
              <label className="block text-sm font-medium mb-2" style={{ color: styles.bodyColor }}>
                Message (optional)
              </label>
              <textarea
                value={donorMessage}
                onChange={(e) => setDonorMessage(e.target.value)}
                placeholder="Leave a message..."
                rows={3}
                maxLength={500}
                className="w-full px-4 py-3 rounded-lg border focus:outline-none resize-none"
                style={{
                  backgroundColor: styles.inputBackground,
                  borderColor: 'rgba(255,255,255,0.1)',
                  color: styles.headingColor,
                }}
              />
            </div>

            {/* Submit */}
            <button
              onClick={handleDonate}
              disabled={processing}
              className="w-full py-3 rounded-lg font-semibold flex items-center justify-center gap-2 transition-all hover:opacity-90 disabled:opacity-60"
              style={styles.getButtonStyles()}
            >
              {processing ? (
                <>
                  <Loader size={18} className="animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <Heart size={18} />
                  Donate ${customAmount || donationAmount}
                </>
              )}
            </button>

            {/* Cancel */}
            <button
              onClick={() => setShowModal(false)}
              disabled={processing}
              className="w-full mt-3 py-2 text-sm font-medium transition-opacity hover:opacity-80 disabled:opacity-50"
              style={{ color: styles.mutedColor }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default DonationsRenderer;
